/*
 * Copyright (c) 2021 - 2026 All rights reserved by Ferenc Farkas <wolferi93@gmail.com>
 */

/**
 * Single source of truth for the "insight" home-screen widgets (finance, bank,
 * sales, operations + user-composed custom widgets). One preset per widget kind;
 * the ids are FINAL — iOS (WidgetKit `kind`) and Android (`react-native-android-widget`
 * name) use them verbatim. The data comes from `GET /api/widgets/insights`.
 *
 * Pure (no React, no I/O) so it is shared by the snapshot builder, the Android
 * renderer, the config screens and Jest. Formatters live in `insightFormat.js`
 * and are re-exported here.
 */

import { humanizeKey } from './insightFormat';

export * from './insightFormat';

/** Periods the backend understands, in display / cycling order. */
export const INSIGHT_PERIODS = ['today', 'yesterday', '7d', '30d', 'mtd', 'lastMonth', 'qtd', 'ytd'];

/** Modules the insights endpoint exposes. */
export const INSIGHT_MODULES = [
    'sales', 'finance', 'crm', 'service', 'support', 'stock', 'projects', 'procurement', 'hr', 'marketing', 'fleet',
];

/** Maximum periods requested in one fetch (backend cap). */
export const MAX_INSIGHT_PERIODS = 4;

/** Option enums. */
export const INSIGHT_CHARTS = ['line', 'area', 'bar'];
export const INSIGHT_STYLES = ['donut', 'bars'];
export const INSIGHT_VIEWS = ['chart', 'list'];
export const INSIGHT_DIRECTIONS = ['all', 'in', 'out'];
export const INSIGHT_SORTS = ['revenue', 'quantity'];
export const INSIGHT_METRICS = ['revenue', 'orders'];
export const INSIGHT_BREAKDOWN_KEYS = ['ordersByType', 'ordersBySource'];

/** Custom-source pattern: `<module>.<key>`. */
const SOURCE_PATTERN = /^([a-z]+)\.([A-Za-z0-9]+)$/;

/**
 * Option validators. `enum` carries `values`; `source` is a `module.key` string;
 * `sources` a bounded array of those; `accountId` a free id string.
 * @type {Record<string, { type: string, values?: string[], max?: number, nullable?: boolean }>}
 */
export const INSIGHT_OPTION_DEFS = {
    period: { type: 'enum', values: INSIGHT_PERIODS },
    chart: { type: 'enum', values: INSIGHT_CHARTS },
    style: { type: 'enum', values: INSIGHT_STYLES },
    view: { type: 'enum', values: INSIGHT_VIEWS },
    direction: { type: 'enum', values: INSIGHT_DIRECTIONS },
    sortBy: { type: 'enum', values: INSIGHT_SORTS },
    metric: { type: 'enum', values: INSIGHT_METRICS },
    breakdownKey: { type: 'enum', values: INSIGHT_BREAKDOWN_KEYS },
    accountId: { type: 'accountId' },
    source: { type: 'source' },
    seriesSource: { type: 'source', nullable: true },
    sources: { type: 'sources', max: 4 },
};

const SIZE_SQUARE = { minWidth: 110, minHeight: 110, targetCellWidth: 2, targetCellHeight: 2 };
const SIZE_WIDE = { minWidth: 250, minHeight: 110, targetCellWidth: 4, targetCellHeight: 2 };
const SIZE_TALL = { minWidth: 250, minHeight: 180, targetCellWidth: 4, targetCellHeight: 3 };

/**
 * Compact preset factory.
 * @param {string} id Final kind id.
 * @param {string} group `finance` | `sales` | `operations` | `custom`.
 * @param {object} spec Remaining preset fields.
 * @returns {object} Preset.
 */
const preset = (id, group, spec) => ({
    id,
    group,
    module: null,
    titleKey: `wi_title_${id}`,
    descriptionKey: `wi_desc_${id}`,
    kpis: [],
    series: [],
    breakdown: null,
    list: null,
    extra: null,
    options: [],
    defaults: {},
    size: SIZE_WIDE,
    ...spec,
});

/**
 * All insight widget presets. Order = display order in the settings screen.
 * @type {Array<object>}
 */
export const INSIGHT_WIDGETS = [
    // ---- Finance
    preset('Finance', 'finance', {
        module: 'finance', icon: 'cash-multiple', sfSymbol: 'banknote', target: 'finance',
        kpis: ['incomeGross', 'expenseGross', 'netResult'], series: ['income', 'expense'],
        options: ['period', 'chart'], defaults: { period: 'mtd', chart: 'bar' },
    }),
    preset('CashFlow', 'finance', {
        module: 'finance', icon: 'swap-vertical-bold', sfSymbol: 'arrow.up.arrow.down', target: 'bank',
        kpis: ['bankInflow', 'bankOutflow'], options: ['period'], defaults: { period: '30d' },
    }),
    preset('BankAccounts', 'finance', {
        module: 'finance', icon: 'bank', sfSymbol: 'building.columns', target: 'bank',
        extra: 'bankAccounts', size: SIZE_TALL, defaults: { period: 'today' },
    }),
    preset('BankTransactions', 'finance', {
        module: 'finance', icon: 'bank-transfer', sfSymbol: 'creditcard', target: 'bank',
        extra: 'bank', options: ['direction', 'accountId'],
        defaults: { direction: 'all', accountId: 'all', period: 'today' }, size: SIZE_TALL,
    }),
    preset('Receivables', 'finance', {
        module: 'finance', icon: 'file-clock-outline', sfSymbol: 'doc.text.badge.clock', target: 'invoices',
        kpis: ['openReceivables', 'overdueReceivables', 'overdueReceivablesCount'],
        breakdown: 'receivablesAging', list: 'overdueReceivables',
        options: ['view'], defaults: { view: 'chart', period: 'today' },
    }),
    preset('Payables', 'finance', {
        module: 'finance', icon: 'file-send-outline', sfSymbol: 'doc.text', target: 'invoices',
        kpis: ['payablesDue7d', 'overduePayables'], breakdown: 'payablesAging', list: 'payablesDue',
        options: ['view'], defaults: { view: 'chart', period: 'today' },
    }),
    preset('Expenses', 'finance', {
        module: 'finance', icon: 'chart-donut', sfSymbol: 'chart.pie', target: 'expenses',
        kpis: ['costsPaid', 'expenseGross'], breakdown: 'expensesByCategory',
        options: ['period', 'style'], defaults: { period: 'mtd', style: 'donut' },
    }),
    // ---- Sales
    preset('SalesChart', 'sales', {
        module: 'sales', icon: 'chart-areaspline', sfSymbol: 'chart.xyaxis.line', target: 'sales',
        kpis: ['revenueGross', 'ordersTotal', 'averageOrderValue'], series: ['revenue', 'orders'],
        options: ['period', 'chart', 'metric'], defaults: { period: '7d', chart: 'area', metric: 'revenue' },
    }),
    preset('PaymentMethods', 'sales', {
        module: 'sales', icon: 'credit-card-outline', sfSymbol: 'creditcard', target: 'sales',
        kpis: ['revenueGross'], breakdown: 'revenueByPaymentType',
        options: ['period', 'style'], defaults: { period: 'today', style: 'donut' },
    }),
    preset('OrderChannels', 'sales', {
        module: 'sales', icon: 'source-branch', sfSymbol: 'arrow.triangle.branch', target: 'sales',
        kpis: ['ordersTotal'], breakdown: 'ordersByType', options: ['period', 'style', 'breakdownKey'],
        defaults: { period: 'today', style: 'donut', breakdownKey: 'ordersByType' },
    }),
    preset('TopProducts', 'sales', {
        module: 'sales', icon: 'trophy-outline', sfSymbol: 'trophy', target: 'sales',
        extra: 'topProducts', options: ['period', 'sortBy'], defaults: { period: '7d', sortBy: 'revenue' },
        size: SIZE_TALL,
    }),
    preset('OpenOrders', 'sales', {
        module: 'sales', icon: 'receipt-text-clock-outline', sfSymbol: 'bag', target: 'sales',
        kpis: ['openOrders', 'openOrdersValue', 'unpaidOrders'], list: 'openOrders',
        defaults: { period: 'today' }, size: SIZE_TALL,
    }),
    // ---- Operations
    preset('CrmPipeline', 'operations', {
        module: 'crm', icon: 'account-filter-outline', sfSymbol: 'person.crop.circle.badge.checkmark', target: 'crm',
        kpis: ['openLeads', 'newLeads', 'conversionRate'], series: ['newLeads'], breakdown: 'leadsByStatus',
        options: ['period', 'view'], defaults: { period: '30d', view: 'chart' },
    }),
    preset('Quotes', 'operations', {
        module: 'crm', icon: 'file-document-edit-outline', sfSymbol: 'doc.richtext', target: 'quotes',
        kpis: ['openQuotes', 'openQuotesValue', 'quotesAccepted', 'acceptedValue'],
        breakdown: 'quotesByStatus', list: 'expiringQuotes',
        options: ['period', 'view'], defaults: { period: 'mtd', view: 'chart' },
    }),
    preset('WorkOrders', 'operations', {
        module: 'service', icon: 'wrench-outline', sfSymbol: 'wrench.and.screwdriver', target: 'workorders',
        kpis: ['openOrders', 'overdueOrders', 'urgentOrders', 'ordersToday'],
        breakdown: 'ordersByStatus', list: 'overdueOrders',
        options: ['period', 'view'], defaults: { period: 'today', view: 'chart' },
    }),
    preset('Support', 'operations', {
        module: 'support', icon: 'lifebuoy', sfSymbol: 'lifepreserver', target: 'support',
        kpis: ['openTickets', 'slaBreachedTickets', 'unassignedTickets', 'urgentOpenTickets'],
        series: ['ticketsCreated', 'ticketsResolved'], breakdown: 'openTicketsByPriority',
        options: ['period', 'view'], defaults: { period: '7d', view: 'chart' },
    }),
    preset('Stock', 'operations', {
        module: 'stock', icon: 'warehouse', sfSymbol: 'shippingbox', target: 'stock',
        kpis: ['lowStock', 'outOfStock', 'stockValue'], breakdown: 'stockHealth', list: 'lowStockItems',
        options: ['view'], defaults: { view: 'chart', period: 'today' },
    }),
    preset('Projects', 'operations', {
        module: 'projects', icon: 'clipboard-text-clock-outline', sfSymbol: 'folder', target: 'projects',
        kpis: ['activeProjects', 'overdueProjects', 'openTasks', 'hoursLogged'],
        series: ['hoursLogged'], breakdown: 'projectsByStatus',
        options: ['period', 'view'], defaults: { period: '7d', view: 'chart' },
    }),
    preset('Procurement', 'operations', {
        module: 'procurement', icon: 'cart-arrow-down', sfSymbol: 'cart', target: 'procurement',
        kpis: ['pendingApprovals', 'openOrders', 'spendInPeriod', 'overdueDeliveries'],
        series: ['spend'], list: 'awaitingMyApproval',
        options: ['period', 'view'], defaults: { period: 'mtd', view: 'chart' },
    }),
    preset('Team', 'operations', {
        module: 'hr', icon: 'account-group-outline', sfSymbol: 'person.3', target: 'hr',
        kpis: ['presentToday', 'workingNow', 'onLeaveToday', 'pendingLeaveRequests'],
        breakdown: 'presenceToday', list: 'workingNow',
        options: ['view'], defaults: { view: 'chart', period: 'today' },
    }),
    preset('Marketing', 'operations', {
        module: 'marketing', icon: 'bullhorn-outline', sfSymbol: 'megaphone', target: 'marketing',
        kpis: ['emailsSent', 'openRate', 'clickRate', 'smsCreditsBalance'], series: ['emailsSent'],
        options: ['period', 'chart'], defaults: { period: '30d', chart: 'bar' },
    }),
    preset('Fleet', 'operations', {
        module: 'fleet', icon: 'car-multiple', sfSymbol: 'car.2', target: 'fleet',
        kpis: ['activeVehicles', 'fuelCost', 'tripDistanceKm', 'maintenanceDue'], series: ['fuelCost'],
        options: ['period', 'chart'], defaults: { period: '30d', chart: 'bar' },
    }),
    // ---- Finance (extended)
    preset('ProfitLoss', 'finance', {
        module: 'finance', icon: 'scale-balance', sfSymbol: 'plusminus.circle', target: 'finance',
        kpis: ['netResult', 'incomeGross', 'expenseGross'], series: ['netResult'],
        options: ['period', 'chart'], defaults: { period: 'mtd', chart: 'area' },
    }),
    preset('BankReconciliation', 'finance', {
        module: 'finance', icon: 'bank-check', sfSymbol: 'checkmark.seal', target: 'bank',
        kpis: ['unmatchedBankTransactions', 'bankInflow', 'bankOutflow'], breakdown: 'bankByMatchStatus',
        list: 'unmatchedBankTransactions', options: ['period', 'view'],
        defaults: { period: '30d', view: 'chart' }, size: SIZE_TALL,
    }),
    preset('VivaSettlements', 'finance', {
        module: 'finance', icon: 'credit-card-check-outline', sfSymbol: 'creditcard', target: 'bank',
        kpis: ['vivaSettled'], breakdown: 'vivaByCategory', list: 'recentVivaSettlements',
        options: ['period', 'view'], defaults: { period: '30d', view: 'chart' },
    }),
    preset('PendingExpenses', 'finance', {
        module: 'finance', icon: 'cash-clock', sfSymbol: 'clock.badge.exclamationmark', target: 'expenses',
        kpis: ['costsPaid', 'expenseGross'], list: 'pendingExpenses',
        options: ['period'], defaults: { period: 'mtd' }, size: SIZE_TALL,
    }),
    preset('InvoicingHealth', 'finance', {
        module: 'finance', icon: 'shield-check-outline', sfSymbol: 'checkmark.shield', target: 'invoices',
        kpis: ['navSubmissionErrors', 'dailyClosingsMissing', 'retentionOverdue'], list: 'navSubmissionErrors',
        defaults: { period: 'today' },
    }),
    // ---- Sales (extended)
    preset('SalesKpis', 'sales', {
        module: 'sales', icon: 'cash-register', sfSymbol: 'dollarsign.circle', target: 'sales',
        kpis: ['revenueGross', 'revenueNet', 'successfulOrders', 'cancelledOrStorno'],
        options: ['period'], defaults: { period: 'today' },
    }),
    preset('OrderStatus', 'sales', {
        module: 'sales', icon: 'format-list-checks', sfSymbol: 'list.bullet.rectangle', target: 'sales',
        kpis: ['openOrders', 'openOrdersValue', 'unpaidOrders'], breakdown: 'openOrdersByStatus', list: 'attentionOrders',
        options: ['view'], defaults: { period: 'today', view: 'chart' }, size: SIZE_TALL,
    }),
    preset('LatestOrders', 'sales', {
        module: 'sales', icon: 'receipt-text-outline', sfSymbol: 'receipt', target: 'sales',
        kpis: ['ordersTotal'], list: 'latestOrders', options: ['period'], defaults: { period: 'today' },
        size: SIZE_TALL,
    }),
    // ---- CRM (extended)
    preset('LeadSources', 'operations', {
        module: 'crm', icon: 'target-account', sfSymbol: 'scope', target: 'crm',
        kpis: ['newLeads', 'convertedLeads', 'conversionRate'], series: ['newLeads'], breakdown: 'leadsBySource',
        options: ['period', 'view'], defaults: { period: '30d', view: 'chart' },
    }),
    preset('FollowUps', 'operations', {
        module: 'crm', icon: 'phone-clock', sfSymbol: 'bell.badge', target: 'crm',
        kpis: ['followUpsDue', 'overdueFollowUps'], list: 'followUpsDue', defaults: { period: 'today' },
        size: SIZE_TALL,
    }),
    preset('Customers', 'operations', {
        module: 'crm', icon: 'account-plus-outline', sfSymbol: 'person.badge.plus', target: 'crm',
        kpis: ['newCustomers', 'totalCustomers'], series: ['newCustomers'],
        options: ['period', 'chart'], defaults: { period: '30d', chart: 'bar' },
    }),
    preset('LatestLeads', 'operations', {
        module: 'crm', icon: 'account-star-outline', sfSymbol: 'person.2', target: 'crm',
        kpis: ['newLeads', 'openLeads'], list: 'latestLeads', options: ['period'], defaults: { period: '7d' },
        size: SIZE_TALL,
    }),
    // ---- Service / work orders
    preset('WorkOrderBoard', 'operations', {
        module: 'service', icon: 'clipboard-list-outline', sfSymbol: 'rectangle.stack', target: 'workorders',
        kpis: ['openOrders', 'newOrders', 'completedOrders', 'avgTurnaroundDays'],
        series: ['newOrders', 'completedOrders'], breakdown: 'ordersByStatus',
        options: ['period', 'view'], defaults: { period: '7d', view: 'chart' },
    }),
    preset('MechanicWorkload', 'operations', {
        module: 'service', icon: 'account-hard-hat', sfSymbol: 'person.crop.square', target: 'workorders',
        kpis: ['unassignedOrders', 'urgentOrders', 'ordersToday'], breakdown: 'mechanicWorkload',
        options: ['style'], defaults: { period: 'today', style: 'bars' },
    }),
    preset('WorkOrderBilling', 'operations', {
        module: 'service', icon: 'file-document-check-outline', sfSymbol: 'doc.badge.gearshape', target: 'workorders',
        kpis: ['awaitingNorm', 'awaitingInvoice', 'revenue'], list: 'activeOrders',
        options: ['period'], defaults: { period: 'mtd' }, size: SIZE_TALL,
    }),
    preset('Appointments', 'operations', {
        module: 'service', icon: 'calendar-clock', sfSymbol: 'calendar.badge.clock', target: 'workorders',
        kpis: ['appointmentsToday', 'ordersToday', 'completedOrders'], options: ['period'],
        defaults: { period: 'today' }, size: SIZE_SQUARE,
    }),
    // ---- Support (extended)
    preset('Complaints', 'operations', {
        module: 'support', icon: 'alert-octagon-outline', sfSymbol: 'exclamationmark.bubble', target: 'support',
        kpis: ['openComplaints', 'newComplaints', 'escalatedComplaints', 'overdueComplaints'],
        series: ['complaintsCreated'], breakdown: 'complaintsByStatus', list: 'escalatedComplaints',
        options: ['period', 'view'], defaults: { period: '30d', view: 'chart' },
    }),
    preset('SupportSla', 'operations', {
        module: 'support', icon: 'timer-sand', sfSymbol: 'timer', target: 'support',
        kpis: ['avgResolutionHours', 'avgFirstResponseHours', 'slaBreachedTickets', 'ticketsDueSoon'],
        list: 'slaBreachedTickets', options: ['period'], defaults: { period: '7d' }, size: SIZE_TALL,
    }),
    // ---- Stock (extended)
    preset('StockLots', 'operations', {
        module: 'stock', icon: 'calendar-alert', sfSymbol: 'calendar.badge.exclamationmark', target: 'stock',
        kpis: ['expiringLots', 'expiredLots'], breakdown: 'lotExpiry', list: 'expiringLots',
        options: ['view'], defaults: { period: 'today', view: 'chart' },
    }),
    preset('StockMovements', 'operations', {
        module: 'stock', icon: 'swap-horizontal', sfSymbol: 'arrow.left.arrow.right', target: 'stock',
        kpis: ['receiptsInPeriod', 'receivedValue', 'movementsInPeriod'], series: ['movements'],
        breakdown: 'movementsByType', options: ['period', 'view'], defaults: { period: '7d', view: 'chart' },
    }),
    preset('StockByWarehouse', 'operations', {
        module: 'stock', icon: 'database-outline', sfSymbol: 'building.2', target: 'stock',
        kpis: ['stockValue', 'stockLines'], breakdown: 'valueByWarehouse',
        options: ['style'], defaults: { period: 'today', style: 'bars' },
    }),
    // ---- Projects (extended)
    preset('TaskBoard', 'operations', {
        module: 'projects', icon: 'checkbox-marked-circle-outline', sfSymbol: 'checklist', target: 'tasks',
        kpis: ['openTasks', 'tasksDueToday', 'overdueTasks', 'tasksCompleted'], breakdown: 'openTasksByPriority',
        list: 'tasksDueToday', options: ['period', 'view'], defaults: { period: '7d', view: 'chart' },
    }),
    preset('ProjectBudget', 'operations', {
        module: 'projects', icon: 'piggy-bank-outline', sfSymbol: 'chart.bar.doc.horizontal', target: 'projects',
        kpis: ['budgetUsage', 'overBudgetProjects', 'atRiskProjects', 'actualHours'], breakdown: 'hoursByProject',
        list: 'overBudgetProjects', options: ['period', 'view'], defaults: { period: '30d', view: 'chart' },
    }),
    // ---- Procurement (extended)
    preset('ReorderSuggestions', 'operations', {
        module: 'procurement', icon: 'cart-plus', sfSymbol: 'cart.badge.plus', target: 'procurement',
        kpis: ['reorderMustOrder', 'reorderSuggested', 'draftOrders'], list: 'reorderSuggestions',
        defaults: { period: 'today' }, size: SIZE_TALL,
    }),
    preset('SpendBySupplier', 'operations', {
        module: 'procurement', icon: 'truck-delivery-outline', sfSymbol: 'chart.bar', target: 'procurement',
        kpis: ['spendInPeriod', 'ordersInPeriod'], series: ['spend'], breakdown: 'spendByPartner',
        options: ['period', 'style'], defaults: { period: 'mtd', style: 'bars' },
    }),
    // ---- HR
    preset('HrAttendance', 'hr', {
        module: 'hr', icon: 'account-clock-outline', sfSymbol: 'person.badge.clock', target: 'hr',
        kpis: ['presentToday', 'workingNow', 'onLeaveToday', 'hoursWorked'], series: ['hoursWorked'],
        breakdown: 'presenceToday', list: 'workingNow', options: ['period', 'view'],
        defaults: { period: 'today', view: 'chart' },
    }),
    preset('HrHeadcount', 'hr', {
        module: 'hr', icon: 'account-group', sfSymbol: 'person.3.sequence', target: 'hr',
        kpis: ['headcount', 'newHires', 'leavers'], breakdown: 'headcountByRelationship',
        options: ['period', 'style'], defaults: { period: '30d', style: 'donut' },
    }),
    preset('HrLeave', 'hr', {
        module: 'hr', icon: 'beach', sfSymbol: 'sun.max', target: 'hr',
        kpis: ['pendingLeaveRequests', 'leaveDaysApproved', 'leaveRequests'], series: ['leaveRequests'],
        breakdown: 'leaveByStatus', list: 'pendingLeaveRequests', options: ['period', 'view'],
        defaults: { period: '30d', view: 'list' }, size: SIZE_TALL,
    }),
    preset('HrAbsences', 'hr', {
        module: 'hr', icon: 'calendar-remove-outline', sfSymbol: 'calendar.badge.minus', target: 'hr',
        kpis: ['onLeaveToday', 'upcomingAbsences'], list: 'upcomingAbsences', defaults: { period: 'today' },
        size: SIZE_TALL,
    }),
    preset('HrCompliance', 'hr', {
        module: 'hr', icon: 'file-certificate-outline', sfSymbol: 'doc.text.magnifyingglass', target: 'hr',
        kpis: ['expiredDocuments', 'expiringDocuments', 'contractsEnding'], list: 'expiringDocuments',
        defaults: { period: 'today' },
    }),
    // ---- Marketing (extended)
    preset('EmailPerformance', 'operations', {
        module: 'marketing', icon: 'email-fast-outline', sfSymbol: 'envelope.badge', target: 'marketing',
        kpis: ['openRate', 'clickRate', 'bounceRate', 'unsubscribes'], series: ['emailsSent'], breakdown: 'engagement',
        options: ['period', 'view'], defaults: { period: '30d', view: 'chart' },
    }),
    preset('Campaigns', 'operations', {
        module: 'marketing', icon: 'bullhorn-variant-outline', sfSymbol: 'paperplane', target: 'marketing',
        kpis: ['campaignsSent', 'scheduledCampaigns', 'draftCampaigns', 'sendFailures'],
        breakdown: 'campaignsByStatus', list: 'scheduledCampaigns', options: ['period', 'view'],
        defaults: { period: '30d', view: 'chart' },
    }),
    preset('SmsUsage', 'operations', {
        module: 'marketing', icon: 'message-text-outline', sfSymbol: 'message', target: 'marketing',
        kpis: ['smsSent', 'smsCreditsUsed', 'smsCreditsBalance'], series: ['smsSent'], breakdown: 'sendsByChannel',
        options: ['period', 'chart'], defaults: { period: '30d', chart: 'bar' },
    }),
    // ---- Fleet (extended)
    preset('FleetMaintenance', 'operations', {
        module: 'fleet', icon: 'car-wrench', sfSymbol: 'wrench', target: 'fleet',
        kpis: ['maintenanceDue', 'maintenanceOverdue', 'expiringDocuments', 'expiredDocuments'],
        list: 'maintenanceDue', defaults: { period: 'today' }, size: SIZE_TALL,
    }),
    preset('FleetFuel', 'operations', {
        module: 'fleet', icon: 'gas-station-outline', sfSymbol: 'fuelpump', target: 'fleet',
        kpis: ['fuelCost', 'refuels', 'fuelQuantity'], series: ['fuelCost'], list: 'recentRefuels',
        options: ['period', 'chart'], defaults: { period: '30d', chart: 'bar' },
    }),
    preset('FleetTrips', 'operations', {
        module: 'fleet', icon: 'map-marker-distance', sfSymbol: 'location', target: 'fleet',
        kpis: ['tripCount', 'tripDistanceKm', 'activeDrivers', 'tripsPendingApproval'], series: ['tripDistance'],
        breakdown: 'vehiclesByStatus', options: ['period', 'chart'], defaults: { period: '30d', chart: 'area' },
    }),
    // ---- Custom (user-composed)
    preset('Metric', 'custom', {
        icon: 'numeric', sfSymbol: 'number', target: 'stats', size: SIZE_SQUARE,
        options: ['source', 'seriesSource', 'period'],
        defaults: { source: 'sales.revenueGross', seriesSource: 'sales.revenue', period: 'today' },
    }),
    preset('KpiGrid', 'custom', {
        icon: 'view-grid-outline', sfSymbol: 'square.grid.2x2', target: 'stats',
        options: ['sources', 'period'],
        defaults: {
            sources: ['sales.revenueGross', 'sales.ordersTotal', 'finance.netResult', 'crm.openLeads'],
            period: 'today',
        },
    }),
    preset('Chart', 'custom', {
        icon: 'chart-line', sfSymbol: 'chart.line.uptrend.xyaxis', target: 'stats',
        options: ['sources', 'period', 'chart'],
        defaults: { sources: ['sales.revenue'], period: '7d', chart: 'area' },
    }),
    preset('Breakdown', 'custom', {
        icon: 'chart-pie', sfSymbol: 'chart.pie', target: 'stats',
        options: ['source', 'period', 'style'],
        defaults: { source: 'sales.revenueByPaymentType', period: 'today', style: 'donut' },
    }),
    preset('Watchlist', 'custom', {
        icon: 'format-list-bulleted', sfSymbol: 'list.bullet', target: 'stats', size: SIZE_TALL,
        options: ['source', 'period'], defaults: { source: 'sales.openOrders', period: 'today' },
    }),
    // ---- Dashboard packs: KpiGrid / Chart clones with a curated cross-module default source set (still fully
    // configurable like the generic kinds - they share the `sources` option, the grid / chart faces and the intents).
    preset('ExecutiveSummary', 'custom', {
        icon: 'view-dashboard-outline', sfSymbol: 'rectangle.grid.2x2', target: 'stats',
        options: ['sources', 'period'],
        defaults: {
            sources: ['sales.revenueGross', 'finance.netResult', 'crm.openLeads', 'hr.workingNow'],
            period: 'today',
        },
    }),
    preset('OperationsPulse', 'custom', {
        icon: 'pulse', sfSymbol: 'waveform.path.ecg', target: 'stats',
        options: ['sources', 'period'],
        defaults: {
            sources: ['service.openOrders', 'support.openTickets', 'stock.lowStock', 'projects.overdueTasks'],
            period: 'today',
        },
    }),
    preset('FinancePulse', 'custom', {
        icon: 'finance', sfSymbol: 'banknote', target: 'stats',
        options: ['sources', 'period'],
        defaults: {
            sources: ['finance.overdueReceivables', 'finance.payablesDue7d', 'finance.bankInflow', 'finance.bankOutflow'],
            period: 'mtd',
        },
    }),
    preset('PeoplePulse', 'custom', {
        icon: 'account-multiple-check-outline', sfSymbol: 'person.3', target: 'stats',
        options: ['sources', 'period'],
        defaults: {
            sources: ['hr.presentToday', 'hr.onLeaveToday', 'hr.pendingLeaveRequests', 'hr.hoursWorked'],
            period: 'today',
        },
    }),
    preset('ProfitTrend', 'custom', {
        icon: 'chart-timeline-variant', sfSymbol: 'chart.xyaxis.line', target: 'stats',
        options: ['sources', 'period', 'chart'],
        defaults: { sources: ['finance.income', 'finance.expense'], period: 'mtd', chart: 'bar' },
    }),
];

const PRESET_INDEX = INSIGHT_WIDGETS.reduce((acc, item) => {
    acc[item.id] = item;

    return acc;
}, {});

/** Ids of every insight kind. */
export const INSIGHT_WIDGET_IDS = INSIGHT_WIDGETS.map((item) => item.id);

/**
 * Look a preset up by id.
 * @param {string} id Kind id.
 * @returns {object|null}
 */
export const getInsightPreset = (id) => PRESET_INDEX[id] || null;

/**
 * True when the id is an insight kind.
 * @param {string} id
 * @returns {boolean}
 */
export const isInsightKind = (id) => Boolean(PRESET_INDEX[id]);

/**
 * Parse a `module.key` source string.
 * @param {string} source
 * @returns {{ module: string, key: string }|null}
 */
export const parseSource = (source) => {
    const match = SOURCE_PATTERN.exec(String(source || ''));
    if (!match || !INSIGHT_MODULES.includes(match[1])) {
        return null;
    }

    return { module: match[1], key: match[2] };
};

/**
 * Validate one option value. Returns `undefined` when invalid (the caller keeps
 * the previous layer's value).
 * @param {string} name Option key.
 * @param {*} value Candidate.
 * @returns {*|undefined}
 */
export const validateOption = (name, value) => {
    const def = INSIGHT_OPTION_DEFS[name];
    if (!def) {
        return undefined;
    }
    switch (def.type) {
        case 'enum':
            return def.values.includes(value) ? value : undefined;
        case 'accountId':
            return typeof value === 'string' && value.length > 0 && value.length <= 64 ? value : undefined;
        case 'source':
            if (value === null || value === '') {
                return def.nullable ? '' : undefined;
            }

            return parseSource(value) ? value : undefined;
        case 'sources': {
            if (!Array.isArray(value)) {
                return undefined;
            }
            const clean = value.filter((entry) => parseSource(entry)).slice(0, def.max);

            return clean.length > 0 ? clean : undefined;
        }
        default:
            return undefined;
    }
};

/**
 * Resolve a widget config: `defaults ← app prefs ← instance config`. Only the
 * options the preset declares (plus keys present in its defaults, e.g. `period`)
 * are honoured; invalid values fall back to the previous layer.
 * @param {string} presetId Kind id.
 * @param {...object} layers Override layers, lowest priority first.
 * @returns {object} Resolved config.
 */
export const resolveInsightConfig = (presetId, ...layers) => {
    const base = getInsightPreset(presetId);
    if (!base) {
        return {};
    }
    const allowed = new Set([...base.options, ...Object.keys(base.defaults)]);
    const config = { ...base.defaults };
    layers.forEach((layer) => {
        if (!layer || typeof layer !== 'object') {
            return;
        }
        Object.keys(layer).forEach((name) => {
            if (!allowed.has(name)) {
                return;
            }
            const valid = validateOption(name, layer[name]);
            if (valid !== undefined) {
                config[name] = valid;
            }
        });
    });

    return config;
};

/**
 * Modules a widget needs data from.
 * @param {object} presetDef Preset.
 * @param {object} [config] Resolved config.
 * @returns {string[]} Unique module ids.
 */
export const insightModulesFor = (presetDef, config = {}) => {
    if (!presetDef) {
        return [];
    }
    const modules = new Set();
    if (presetDef.module) {
        modules.add(presetDef.module);
    }
    [config.source, config.seriesSource, ...(Array.isArray(config.sources) ? config.sources : [])].forEach((source) => {
        const parsed = parseSource(source);
        if (parsed) {
            modules.add(parsed.module);
        }
    });

    return Array.from(modules);
};

/**
 * Pick a module block from a payload.
 * @param {object} payload Insights payload.
 * @param {string} period Period id.
 * @param {string} module Module id.
 * @returns {object|null}
 */
export const pickModule = (payload, period, module) => payload?.periods?.[period]?.modules?.[module] || null;

/** Module status for a period (`ok|forbidden|timeout|error`, or null when absent). */
export const moduleStatus = (payload, period, module) => pickModule(payload, period, module)?.status || null;

/** Pick a KPI `{ value, previous, unit, currency? }` or null. */
export const pickKpi = (payload, period, module, key) => {
    const block = pickModule(payload, period, module);

    return block?.status === 'ok' ? (block.kpis?.[key] || null) : null;
};

/** Pick a series `{ unit, currency?, points, compare? }` or null. */
export const pickSeries = (payload, period, module, key) => {
    const block = pickModule(payload, period, module);

    return block?.status === 'ok' ? (block.series?.[key] || null) : null;
};

/** Pick a breakdown `{ unit, currency?, items }` or null. */
export const pickBreakdown = (payload, period, module, key) => {
    const block = pickModule(payload, period, module);

    return block?.status === 'ok' ? (block.breakdowns?.[key] || null) : null;
};

/** Pick a list `{ total, items }` or null. */
export const pickList = (payload, period, module, key) => {
    const block = pickModule(payload, period, module);

    return block?.status === 'ok' ? (block.lists?.[key] || null) : null;
};

/**
 * Percent change vs the previous value: (value - previous) / |previous|.
 * @param {number|null} value
 * @param {number|null} previous
 * @returns {number|null} Fraction (0.12 = +12%) or null when not computable.
 */
export const deltaPercent = (value, previous) => {
    if (typeof value !== 'number' || typeof previous !== 'number'
        || !Number.isFinite(value) || !Number.isFinite(previous) || previous === 0) {
        return null;
    }

    return (value - previous) / Math.abs(previous);
};

/**
 * Nearest period actually present in the payload (callers must show the
 * returned id's label — never the requested one).
 * @param {object} payload Insights payload.
 * @param {string} wanted Requested period.
 * @returns {string|null}
 */
export const nearestAvailablePeriod = (payload, wanted) => {
    const available = Object.keys(payload?.periods || {}).filter((id) => INSIGHT_PERIODS.includes(id));
    if (available.length === 0) {
        return null;
    }
    if (available.includes(wanted)) {
        return wanted;
    }
    const target = INSIGHT_PERIODS.indexOf(wanted);
    const ranked = [...available].sort(
        (a, b) => Math.abs(INSIGHT_PERIODS.indexOf(a) - target) - Math.abs(INSIGHT_PERIODS.indexOf(b) - target),
    );

    return ranked[0];
};

/**
 * Next period (cycling through INSIGHT_PERIODS) that exists in the payload.
 * @param {string} current
 * @param {object} payload
 * @returns {string|null} Null when no other period is available.
 */
export const nextAvailablePeriod = (current, payload) => {
    const available = Object.keys(payload?.periods || {});
    const start = Math.max(0, INSIGHT_PERIODS.indexOf(current));
    for (let step = 1; step <= INSIGHT_PERIODS.length; step += 1) {
        const candidate = INSIGHT_PERIODS[(start + step) % INSIGHT_PERIODS.length];
        if (candidate !== current && available.includes(candidate)) {
            return candidate;
        }
    }

    return null;
};

/**
 * Build the picker catalog (`module.key` entries that are present and ok).
 * @param {object} payload Insights payload.
 * @param {(kind: string, module: string, key: string) => string} [labelFn] Label resolver.
 * @returns {{ kpis: object[], series: object[], breakdowns: object[], lists: object[] }}
 */
export const buildInsightCatalog = (payload, labelFn) => {
    const seen = { kpis: new Map(), series: new Map(), breakdowns: new Map(), lists: new Map() };
    const kindOf = { kpis: 'kpi', series: 'series', breakdowns: 'breakdown', lists: 'list' };
    Object.keys(payload?.periods || {}).forEach((period) => {
        const modules = payload.periods[period]?.modules || {};
        Object.keys(modules).forEach((module) => {
            const block = modules[module];
            if (!block || block.status !== 'ok') {
                return;
            }
            Object.keys(seen).forEach((bucket) => {
                Object.keys(block[bucket] || {}).forEach((key) => {
                    const id = `${module}.${key}`;
                    if (!seen[bucket].has(id)) {
                        const unit = block[bucket][key]?.unit || (bucket === 'lists' ? 'count' : '');
                        const label = typeof labelFn === 'function' ? labelFn(kindOf[bucket], module, key) : '';
                        seen[bucket].set(id, { id, label: label || humanizeKey(key), module, unit });
                    }
                });
            });
        });
    });

    return {
        kpis: Array.from(seen.kpis.values()),
        series: Array.from(seen.series.values()),
        breakdowns: Array.from(seen.breakdowns.values()),
        lists: Array.from(seen.lists.values()),
    };
};

/**
 * Union of requested periods: most-used first, ties by canonical order, capped,
 * and ALWAYS including `today`.
 * @param {string[][]} periodLists One list per widget (preset default or instance).
 * @param {number} [cap]
 * @returns {string[]}
 */
export const unionInsightPeriods = (periodLists, cap = MAX_INSIGHT_PERIODS) => {
    const counts = new Map();
    (periodLists || []).forEach((list) => {
        (Array.isArray(list) ? list : []).forEach((period) => {
            if (INSIGHT_PERIODS.includes(period)) {
                counts.set(period, (counts.get(period) || 0) + 1);
            }
        });
    });
    const ranked = Array.from(counts.keys())
        .filter((period) => period !== 'today')
        .sort((a, b) => (counts.get(b) - counts.get(a)) || (INSIGHT_PERIODS.indexOf(a) - INSIGHT_PERIODS.indexOf(b)));

    return ['today', ...ranked].slice(0, Math.max(1, cap));
};

/**
 * JSON-safe preset for the snapshot (`insights.presets[id]`).
 * @param {object} presetDef
 * @param {object} config Resolved app-level config.
 * @returns {object}
 */
export const toSnapshotPreset = (presetDef, config) => ({
    ...JSON.parse(JSON.stringify(presetDef)),
    config,
});
